import { parse, ParsedUrlQueryInput } from 'querystring';

import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import { config } from 'config/config';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Button, Checkbox, Form, notification, Row } from 'antd';
import routePaths, { extractParamsFromChallengeDetail } from 'config/routes';
import { NextPage } from 'next';
import classNames from 'classnames';
import Router, { useRouter } from 'next/router';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { getAccountStatus, postDataCall } from 'services/api';
import apiPaths from 'services/apiPaths';
import redirectTo from 'services/redirectTo';
import {
  login,
  resetUserStatus,
  setAccountStatus,
  setAccountSubscription,
  updateMCSubscription,
} from 'src/actions/authActions';
import { FormItemConsumer } from 'src/components/forms/consumer/FormItemConsumer';
import RememberPasswordForm from 'src/components/forms/consumer/RememberPasswordFormConsumer';
import { openToast } from 'src/components/general/Toast';
import ValidateTermsModal from 'src/components/login/consumer/ValidateTermsModal';
import { IRootReducers } from 'src/reducers';
import {
  ConsumerAccountStatusEnum,
  LoginEnum,
  MCUserStatus,
  PlatformEnum,
} from 'src/shared/enums';
import {
  customFormatMessage,
  dispatchPageViewEvent,
  decryptValue,
  encryptValue,
  getEmailFromQuery,
  removeWhitespaces,
  virtualUrlInit,
  useWindowSize,
} from 'utils';

const { INDEX } = routePaths;
const { CON_SPAIN } = PlatformEnum;

const {
  NOT_FOUND,
  NOT_VALIDATED,
  REGISTERED,
  REGISTERED_AND_VALIDATED,
  VALIDATED,
  WAITING_DERMO_INFO,
} = ConsumerAccountStatusEnum;
interface OwnProps {
  children?: React.ReactNode;
  buttonRef?: React.MutableRefObject<any>;
  pregnancyLanding?: boolean;
  loginRef: () => React.MutableRefObject<any>;
  showUserPregnancyLanding?: React.Dispatch<SetStateAction<boolean>>;
}

const mapStateToProps = (state: IRootReducers) => {
  return {
    accountStatus: state.auth.accountStatus,
    newsletter: state.auth.accountSubscription,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      login,
      resetUserStatus,
      setAccountStatus,
      setAccountSubscription,
      updateMCSubscription,
    },
    dispatch,
  );

type LoginProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

const LoginForm: NextPage<LoginProps, {}> = ({
  accountStatus,
  newsletter,
  login,
  loginRef,
  buttonRef,
  setAccountStatus,
  pregnancyLanding,
  setAccountSubscription,
  updateMCSubscription,
  showUserPregnancyLanding,
  resetUserStatus,
}: LoginProps): JSX.Element => {
  const router = useRouter();
  const { query } = router;
  const [form] = Form.useForm();
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showNotValidated, setShowNotValidated] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(null);
  const [showNewsletter, setShowNewsletter] = useState<boolean>(false);
  const firstView = useRef(true);
  const email = decryptValue(getEmailFromQuery(query?.email));
  const isDermo = !!query.dermo && query.dermo === 'true';
  const isCampaign = !!query.campaign;
  const isPregnancy = !!query.pregnancy && query.pregnancy === 'true';
  const getCurrentUrl = () => {
    return window.location.href;
  };
  const { isLGBG } = useWindowSize();
  const [observeNewsletter, setObserveNewsletter] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(true);
  const ref = useRef(null);
  useEffect(() => {
    if (pregnancyLanding) {
      const observerEmail = new IntersectionObserver(([entry]) => {
        setButtonVisible(entry.isIntersecting);
      });
      const observerNewsletter = new IntersectionObserver(([entry]) => {
        setObserveNewsletter(entry.isIntersecting);
      });

      observerEmail.observe(buttonRef.current);

      if (showLogin) {
        observerNewsletter.observe(ref.current);
      }
    }
  }, [buttonRef, ref]);

  const stickyButton = () => {
    if (showLogin) {
      if (observeNewsletter) return false;
      else if (buttonVisible) {
        return false;
      } else return true;
    } else if (buttonVisible) return false;
    else return true;
  };

  const campaignContainerStyles = classNames(
    { loginForm__loginContainer: !isCampaign },
    { 'loginForm__loginContainer-campaign': isCampaign },
  );

  const campaignButtonStyles = classNames(
    {
      'loginForm-con__btnLogin--customer-login btn-primary btn-primary--black btn-continue':
        !isCampaign,
    },
    {
      'loginForm-con__btnLogin--customer-login-discovery btn-primary btn-primary--black btn-continue':
        isCampaign,
    },
  );

  const campaignTextBoxStyles = classNames(
    { 'login-con-message': !isCampaign },
    { 'login-con-message discovery': isCampaign },
  );

  useEffect(() => {
    if (accountStatus)
      switch (accountStatus) {
        case REGISTERED:
          setShowNotValidated(true);
          break;
        case REGISTERED_AND_VALIDATED:
          setShowNotValidated(false);
          setShowLogin(true);
          break;
      }
  }, [accountStatus, newsletter]);

  useEffect(() => {
    if (showNotValidated) {
      firstView.current = false;
      dispatchPageViewEvent(router, {
        ...virtualUrlInit,
        pageType: 'login',
        detail: 'pending-validation',
      });
    }
  }, [showNotValidated]);

  const handleOnClick = () => {
    form.validateFields();
    document
      .getElementById('emailForm')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const redirectAfterLogin = () => {
    const redirectUrl = query.redirect;
    let destinationPathname: string;
    let destinationQuery: ParsedUrlQueryInput = {};

    if (query.campaign) {
      destinationQuery.campaign = query.campaign;
      redirectTo(INDEX, undefined, Router, destinationQuery);
    }

    if (query.pregnancy) {
      if (config.APP.PLATFORM === CON_SPAIN) {
        destinationQuery.pregnancy = query.pregnancy;
        showUserPregnancyLanding(false);
      }
      redirectTo(INDEX, undefined, Router, destinationQuery);
    }

    if (query.dermo && (!redirectUrl || typeof redirectUrl !== 'string')) {
      destinationQuery.dermo = query.dermo;
      redirectTo(INDEX, undefined, Router, destinationQuery);
      return;
    } else if (!redirectUrl || typeof redirectUrl !== 'string') {
      redirectTo(INDEX, undefined, Router);
      return;
    }

    if (redirectUrl.indexOf('?') >= 0) {
      const splitPath = redirectUrl.split('?');
      destinationPathname = splitPath[0];
      destinationQuery = parse(splitPath[1]);
    } else {
      destinationPathname = redirectUrl;
    }

    const challengeDetailParams =
      extractParamsFromChallengeDetail(destinationPathname);

    if (challengeDetailParams) {
      const [detailPath, id] = challengeDetailParams;
      destinationPathname = detailPath;
      destinationQuery.id = id;
    }

    redirectTo(destinationPathname, undefined, Router, destinationQuery);
    return;
  };

  const handleSubmit = async (values: any) => {
    if (!showLogin) {
      const {
        data: { accountStatus, newsletter, hubCustomer },
      } = await getAccountStatus({
        email: values.email,
        dermo: isDermo,
      });

      let data: any = {
        email: encryptValue(values.email),
        shallow: true,
        scroll: true,
        utm_campaign: query?.utm_campaign ?? '',
        pregnancy: isPregnancy ?? '',
      };

      if (isDermo) {
        data.dermo = isDermo;
      }

      setAccountStatus(accountStatus ?? 0, hubCustomer ?? false);
      setAccountSubscription(newsletter ?? false);
      switch (accountStatus) {
        case NOT_FOUND:
        case NOT_VALIDATED:
        case VALIDATED:
          redirectTo(routePaths.PAGES.SIGNUP, undefined, Router, data);
          break;
        case REGISTERED:
          setShowNotValidated(true);
          break;
        case WAITING_DERMO_INFO:
        case REGISTERED_AND_VALIDATED:
          setShowNotValidated(false);
          setShowLogin(true);
          handleShowNewsletter(newsletter);
          break;
      }
    } else {
      try {
        setLoading(true);
        await login({
          email: values.email,
          password: values.password,
          rememberMe: values.rememberMe,
          validateTerms: false,
          redirect: false,
        });
        if (values.newsletter) {
          await handleSubscription(values.newsletter);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        redirectAfterLogin();
      } catch (response) {
        if (!response.error) return;
        const { error, message } = response;
        let customMessage;
        switch (error) {
          case LoginEnum.ERROR_INVALID_GRANT:
            customMessage = formatMessage({
              id: 'login.sign-in-error.incorrect-password',
            });
            break;
          case LoginEnum.ERROR_TERMS_NOT_ACCEPTED:
            setTermsAccepted(false);
            break;
          default:
            customMessage = error || message;
            break;
        }

        if (customMessage) {
          notification.error({
            message: formatMessage(
              { id: 'login.sign-in-error' },
              { message: customMessage },
            ),
          });
        }

        dispatchPageViewEvent(router, {
          ...virtualUrlInit,
          detail: 'error',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleShowNewsletter = (newsletter: boolean) => {
    if (!newsletter) {
      setShowNewsletter(true);
    }
  };

  const handleSubscription = async (newsletter: boolean) => {
    const { UNSUBSCRIBE, SUBSCRIBE } = apiPaths.MC_NEWSLETTER;

    try {
      await postDataCall({
        dataPath: newsletter ? SUBSCRIBE : UNSUBSCRIBE,
        data: {
          utmCampaign: query?.utm_campaign ?? '',
          isDermo: isDermo,
          currentUrl: getCurrentUrl(),
        },
        callConfig: {},
      });

      updateMCSubscription(
        newsletter ? MCUserStatus.SUBSCRIBED : MCUserStatus.UNSUBSCRIBED,
        newsletter ? MCUserStatus.SUBSCRIBED : MCUserStatus.UNSUBSCRIBED,
      );
    } catch (error) {
      console.log(error);
    }
  };

  const sendValidationEmail = async () => {
    try {
      let payload = {};
      if (isDermo) {
        payload = {
          email: form.getFieldValue('email'),
          dermo: isDermo,
        };
      } else {
        payload = {
          email: form.getFieldValue('email'),
        };
      }
      await postDataCall({
        dataPath: apiPaths.AUTH.SEND_VALIDATION_EMAIL,
        data: payload,
        callConfig: {},
      });
      openToast(formatMessage({ id: 'login.send-validate-succes' }), 'success');
    } catch (error) {
      openToast(
        formatMessage({ id: 'login.send-validate-error' }),
        'error',
        6,
        'cross-red',
        'increase_30',
      );
    }
  };

  const handleResetLogin = () => {
    resetUserStatus();
    setAccountSubscription(false);
    setShowNotValidated(false);
    setShowLogin(false);
  };

  // Reset prelogin data on route change
  useEffect(() => {
    handleResetLogin();
    return () => handleResetLogin();
  }, []);

  return (
    <>
      <Form
        noValidate
        form={form}
        name="login"
        layout="vertical"
        className="loginForm-con loginForm-con-wrapper"
        onFinish={handleSubmit}>
        <div ref={buttonRef} />
        <div id={'emailForm'}>
          <Row className="input-row loginForm-con--email-row">
            <Form.Item
              name="email"
              initialValue={email}
              rules={[
                {
                  required: true,
                  type: 'string',
                  transform: (v) => removeWhitespaces(v),
                  message: formatMessage(
                    { id: 'form.field.required' },
                    { name: formatMessage({ id: 'form.field.email' }) },
                  ),
                },
                {
                  type: 'email',
                  transform: (v) => removeWhitespaces(v),
                  validateTrigger: 'onClick',
                  message: formatMessage(
                    { id: 'register.form.email-not-valid' },
                    { name: 'email' },
                  ),
                },
              ]}>
              {/*TODO: Default value must be the same if it comes from the landing or email*/}
              <FormItemConsumer
                options={{
                  label: 'form.field.email.label',
                  inputOptions: {
                    id: 'standard-required--user',
                    type: 'email',
                    disabled: Boolean(accountStatus),
                  },
                }}
              />
            </Form.Item>
            {showLogin ? (
              <Button type="text" onClick={handleResetLogin}>
                {customFormatMessage('button.edit')}
              </Button>
            ) : (
              <></>
            )}
          </Row>
        </div>
        {!showLogin && !showNotValidated && !isCampaign && !isPregnancy && (
          <div className={campaignTextBoxStyles}>
            {customFormatMessage('login.account')}
          </div>
        )}
        {showLogin && (
          <>
            <Row className="input-row">
              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    type: 'string',
                    message: formatMessage(
                      { id: 'form.field.required' },
                      { name: formatMessage({ id: 'form.field.password' }) },
                    ),
                  },
                ]}>
                <FormItemConsumer
                  options={{
                    label: 'form.field.password',
                    inputOptions: {
                      id: 'standard-required--password',
                      type: 'password',
                      }
                  }}
                />
              </Form.Item>
            </Row>
            <div className="checkbox-remember-container" ref={ref}>
              <RememberPasswordForm loginRef={loginRef} email={form.getFieldValue("email")}/>
            </div>
            <Form.Item
              name="rememberMe"
              className="checkbox-remember-login login-checkbox"
              valuePropName="checked"
              initialValue={false}>
              <Checkbox>
                <FormattedMessage id="login.remember.me" />
              </Checkbox>
            </Form.Item>
            {showNewsletter && !isCampaign && !isPregnancy && (
              <Form.Item
                name="newsletter"
                className="login-checkbox"
                valuePropName="checked">
                <Checkbox>
                  <FormattedMessage id="form.checkbox.subscribe-newsletter-comercial" />
                </Checkbox>
              </Form.Item>
            )}
            {showNewsletter && !isCampaign && !isPregnancy && (
              <Row className="register-terms-conditions">
                <p>
                  {customFormatMessage('form.privacy.policy', {
                    privacy: function setPrivacy(chunks: any) {
                      return (
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href={`static?page=${config.CON.PAGES.PRIVACY}`}
                          className="link underline-text">
                          {chunks}
                        </a>
                      );
                    },
                  })}
                </p>
              </Row>
            )}
          </>
        )}
        <Row className={campaignContainerStyles}>
          <Form.Item shouldUpdate>
            {() => (
              <div
                className={
                  pregnancyLanding && stickyButton()
                    ? 'container-continue-button'
                    : ''
                }>
                <Button
                  onClick={handleOnClick}
                  htmlType="submit"
                  className={
                    pregnancyLanding
                      ? `${
                          !observeNewsletter && !buttonVisible && !isLGBG
                            ? 'btn-secondary'
                            : ''
                        } btn-primary-pregnancy btn-primary--black qr-landing__login__form__button`
                      : campaignButtonStyles
                  }
                  loading={loading}
                  disabled={
                    !pregnancyLanding &&
                    (!form.getFieldValue('email') ||
                      !!form
                        .getFieldsError()
                        .filter(({ errors }) => errors.length).length ||
                      showNotValidated)
                  }
                  block>
                  {isCampaign ? (
                    <FormattedMessage id="consumable.coupon.discovery.button" />
                  ) : (
                    <>
                      <FormattedMessage id="app.continue" />
                      {!pregnancyLanding && (
                        <ArrowRightOutlined className="arrow-right-justify" />
                      )}
                    </>
                  )}
                </Button>
              </div>
            )}
          </Form.Item>
        </Row>
        {showNotValidated && (
          <>
            <div className="loginForm-con__extra-information">
              {customFormatMessage('landing.qr.login.form.not.validated', {
                button: function setButton(chunks: any) {
                  return (
                    <button
                      type="button"
                      onClick={sendValidationEmail}
                      className="send-email-button">
                      {chunks}
                    </button>
                  );
                },
              })}
            </div>
            <p className="loginForm-con__contact">
              {customFormatMessage('login.doubt')}
            </p>
          </>
        )}
      </Form>
      <ValidateTermsModal
        termsAccepted={termsAccepted}
        setTermsAccepted={setTermsAccepted}
        email={form.getFieldValue('email')}
        password={form.getFieldValue('password')}
        login={login}
        rememberMe={form.getFieldValue('rememberMe')}
      />
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
